import { createClient } from '@supabase/supabase-js';

export interface PremiumUser {
  email: string;
  status: 'trial' | 'premium' | 'expired';
  startDate: string;
  endDate: string;
  autoRenew: boolean;
}

interface PaystackResponse {
  status: boolean;
  message: string;
  data: {
    authorization_url: string;
    access_code: string;
    reference: string;
  };
}

class SubscriptionService {
  private readonly PAYSTACK_SECRET_KEY = process.env.REACT_APP_PAYSTACK_SECRET_KEY!;
  private readonly JWT_KEY = 'premium_token';
  private supabase = createClient(process.env.REACT_APP_SUPABASE_URL!, process.env.REACT_APP_SUPABASE_ANON_KEY!);
  private readonly TABLE_NAME = 'premium_users';

  constructor() {
    // Initialize the service
    this.checkTableExists();
  }

  private async checkTableExists() {
    try {
      await this.supabase.from(this.TABLE_NAME).select('*').limit(1);
    } catch (error) {
      console.error(`Error checking table ${this.TABLE_NAME}:`, error);
    }
  }

  private async getPremiumUsers(): Promise<Record<string, PremiumUser>> {
    try {
      const { data, error } = await this.supabase.from(this.TABLE_NAME).select('*');
      if (error) {
        console.error('Error loading premium users:', error);
        return {};
      }
      return data.reduce((acc: Record<string, PremiumUser>, user: PremiumUser) => {
        acc[user.email] = user;
        return acc;
      }, {});
    } catch (error) {
      console.error('Error in getPremiumUsers:', error);
      return {};
    }
  }

  async initializePayment(email: string, amount: number): Promise<string> {
    try {
      const response = await fetch('https://api.paystack.co/transaction/initialize', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.PAYSTACK_SECRET_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          amount: amount * 100,
          callback_url: `${window.location.origin}/payment/verify`
        })
      });
      const data: PaystackResponse = await response.json();
      if (!data.status) throw new Error(data.message);
      return data.data.authorization_url;
    } catch (error) {
      console.error('Payment initialization failed:', error);
      throw error;
    }
  }

  async verifyPayment(reference: string): Promise<boolean> {
    try {
      const response = await fetch(`https://api.paystack.co/transaction/verify/${reference}`, {
        headers: { 'Authorization': `Bearer ${this.PAYSTACK_SECRET_KEY}` }
      });
      const data = await response.json();
      return data.status && data.data.status === 'success';
    } catch (error) {
      console.error('Payment verification failed:', error);
      throw error;
    }
  }

  private storeToken(token: string) {
    localStorage.setItem(this.JWT_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.JWT_KEY);
  }

  async isPremiumActive(email: string): Promise<boolean> {
    const users = await this.getPremiumUsers();
    const user = users[email];
    return !!user && user.status === 'premium' && new Date() < new Date(user.endDate);
  }

  async updateSubscription(email: string, status: 'trial' | 'premium'): Promise<PremiumUser> {
    try {
      const now = new Date();
      const user: PremiumUser = {
        email,
        status,
        startDate: now.toISOString(),
        endDate: new Date(now.getTime() + (status === 'premium' ? 30 : 1) * 86400000).toISOString(),
        autoRenew: status === 'premium'
      };
      await this.savePremiumUsers({ [email]: user });
      if (status === 'premium') this.storeToken(this.generateToken(email));
      return user;
    } catch (error) {
      console.error('Error in updateSubscription:', error);
      throw error;
    }
  }

  private generateToken(email: string): string {
    const token = {
      sub: email,
      role: 'premium',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 2592000
    };
    return btoa(JSON.stringify(token));
  }

  async checkSubscriptionStatus(email: string): Promise<{ status: 'trial' | 'premium' | 'expired'; daysLeft: number }> {
    try {
      const users = await this.getPremiumUsers();
      const user = users[email];
      
      if (!user) return { status: 'expired', daysLeft: 0 };
      
      const endDate = new Date(user.endDate);
      const daysLeft = Math.ceil((endDate.getTime() - Date.now()) / 86400000);
      
      // If expired, update status in database
      if (daysLeft <= 0) {
        // Only update if not already expired
        if (user.status !== 'expired') {
          user.status = 'expired';
          await this.savePremiumUsers({ [email]: user });
          
          // Also update local cache
          const storedUsers = localStorage.getItem('premium_users');
          if (storedUsers) {
            try {
              const users = JSON.parse(storedUsers);
              if (users[email]) {
                users[email].status = 'expired';
                localStorage.setItem('premium_users', JSON.stringify(users));
              }
            } catch (e) {
              console.error('Error updating local storage:', e);
            }
          }
        }
        return { status: 'expired', daysLeft: 0 };
      }
      
      return { status: user.status, daysLeft };
    } catch (error) {
      console.error('Error checking subscription status:', error);
      // Fail safe - if we can't verify, assume expired
      return { status: 'expired', daysLeft: 0 };
    }
  }

  private syncToLocalStorage(users: Record<string, PremiumUser>): void {
    try {
      localStorage.setItem('premium_users', JSON.stringify(users));
    } catch (error) {
      console.error('Error syncing to localStorage:', error);
    }
  }

  private async savePremiumUsers(users: Record<string, PremiumUser>) {
    try {
      const userArray = Object.values(users);
      const { error } = await this.supabase.from(this.TABLE_NAME).upsert(userArray);
      if (error) {
        console.error('Error saving premium users:', error);
        throw error;
      }
      
      // Get all users and sync to localStorage
      const allUsers = await this.getPremiumUsers();
      this.syncToLocalStorage(allUsers);
    } catch (error) {
      console.error('Error in savePremiumUsers:', error);
      throw error;
    }
  }

  async getAllPremiumUsers(): Promise<PremiumUser[]> {
    return Object.values(await this.getPremiumUsers());
  }

  async cancelSubscription(email: string): Promise<boolean> {
    const users = await this.getPremiumUsers();
    if (!users[email]) return false;
    users[email] = { ...users[email], status: 'expired', autoRenew: false, endDate: new Date().toISOString() };
    await this.savePremiumUsers(users);
    return true;
  }

  async getCurrentSubscription(email: string): Promise<PremiumUser | null> {
    const users = await this.getPremiumUsers();
    return users[email] || null;
  }

  async getSubscriptionHistory(userId: string): Promise<any[]> {
    return [];
  }

  private async logSubscriptionChange(userId: string, status: string): Promise<void> {}
}

export const subscriptionService = new SubscriptionService();
